import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { EditSquare } from '@/components/icons/EditSquare';
import { PanelLeftClose, ChevronRight, Building2, MapPin, Train, Leaf } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useChatInteract } from '@chainlit/react-client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import logoDark from '@/assets/logo_dark.svg';
import logoLight from '@/assets/logo_light.svg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const stateOptions = [
  { id: 'haryana', name: 'Haryana', folderName: 'haryana' },
  // { id: 'bangalore', name: 'Bangalore', folderName: 'bangalore' },
  { id: 'delhi', name: 'New Delhi', folderName: 'delhi' },
  { id: 'uttar-pradesh', name: 'Uttar Pradesh', folderName: 'UP' }
];



const sustainabilityOptions = [
  { id: 'leed', name: 'LEED', folderName: 'LEED' },
  // { id: 'griha', name: 'GRIHA', folderName: 'GRIHA' }
];

interface LeftSidebarProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCollapse?: () => void;
  showCollapseButton?: boolean;
}

function SidebarContent({ onCollapse, showCollapseButton }: { onCollapse?: () => void; showCollapseButton?: boolean }) {
  const [selectedState, setSelectedState] = useState<string>('');
  const [nbcSelected, setNbcSelected] = useState<boolean>(false);
  const [selectedSustainability, setSelectedSustainability] = useState<string>('');
  const [todSelected, setTodSelected] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { clear, updateChatSettings } = useChatInteract();

  // Detect theme
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Function to send current selections to backend
  const sendSettingsToBackend = (state: string, nbc: boolean, sustainability: string, tod: boolean) => {
    // Convert IDs to folder names that backend expects
    const stateFolderName = state ? stateOptions.find(opt => opt.id === state)?.folderName : undefined;
    const sustainabilityFolderName = sustainability ? sustainabilityOptions.find(opt => opt.id === sustainability)?.folderName : undefined;
    
    const settings = {
      state_bylaws: stateFolderName,
      nbc_selected: nbc,
      sustainability_standards: sustainabilityFolderName ? [sustainabilityFolderName] : [],
      tod_selected: tod
    };
    
    console.log('Sending settings to backend:', settings);
    updateChatSettings(settings);
  };

  // Listen for clear document selections event
  useEffect(() => {
    const handleClearDocumentSelections = () => {
      setSelectedState('');
      setNbcSelected(false);
      setSelectedSustainability('');
      setTodSelected(false);
      
      sendSettingsToBackend('', false, '', false);
    };

    window.addEventListener('clearDocumentSelections', handleClearDocumentSelections);

    return () => {
      window.removeEventListener('clearDocumentSelections', handleClearDocumentSelections);
    };
  }, []);

  // Listen for document selection updates from example queries
  useEffect(() => {
    const handleUpdateDocumentSelections = (event: any) => {
      const { state_bylaws, nbc_selected, sustainability_standards, tod_selected } = event.detail;
      
      // Map backend folder names to frontend IDs
      let stateId = '';
      if (state_bylaws) {
        const stateOption = stateOptions.find(opt => opt.folderName.toLowerCase() === state_bylaws.toLowerCase());
        stateId = stateOption?.id || '';
      }
      
      let sustainabilityId = '';
      if (sustainability_standards && sustainability_standards.length > 0) {
        const susOption = sustainabilityOptions.find(opt => opt.folderName === sustainability_standards[0]);
        sustainabilityId = susOption?.id || '';
      }
      
      setSelectedState(stateId);
      setNbcSelected(nbc_selected || false);
      setSelectedSustainability(sustainabilityId);
      setTodSelected(tod_selected || false);
    };

    window.addEventListener('updateDocumentSelections', handleUpdateDocumentSelections);

    return () => {
      window.removeEventListener('updateDocumentSelections', handleUpdateDocumentSelections);
    };
  }, []);

  const handleNewChat = () => {
    setSelectedState('');
    setNbcSelected(false);
    setSelectedSustainability('');
    setTodSelected(false);
    
    sendSettingsToBackend('', false, '', false);
    
    clear();
    navigate('/');
  };

  const handleStateSelect = (stateId: string) => {
    const newState = selectedState === stateId ? '' : stateId;
    setSelectedState(newState);
    sendSettingsToBackend(newState, nbcSelected, selectedSustainability, todSelected);
  };

  const handleNbcToggle = () => {
    const newNbcSelected = !nbcSelected;
    setNbcSelected(newNbcSelected);
    sendSettingsToBackend(selectedState, newNbcSelected, selectedSustainability, todSelected);
  };

  const handleSustainabilitySelect = (sustainabilityId: string) => {
    const newSustainability = selectedSustainability === sustainabilityId ? '' : sustainabilityId;
    setSelectedSustainability(newSustainability);
    sendSettingsToBackend(selectedState, nbcSelected, newSustainability, todSelected);
  };

  const handleTodToggle = () => {
    const newTodSelected = !todSelected;
    setTodSelected(newTodSelected);
    sendSettingsToBackend(selectedState, nbcSelected, selectedSustainability, newTodSelected);
  };

  return (
    <div ref={containerRef} className="h-full bg-sidebar text-sidebar-foreground border-r-2 border-sidebar-border">
      {/* Header */}
      <div className="relative flex justify-between items-center py-3 px-3 min-h-[100px]">
        <div className="absolute left-4 right-4 bottom-0 h-0.5 bg-sidebar-border"></div>
        <div className="flex-1 flex justify-center">
          <img
            src={isDark ? logoLight : logoDark}
            alt="Logo"
            className="w-full h-auto object-contain max-w-[160px] min-w-[200px]"
          />
        </div>
        {showCollapseButton && onCollapse && (
          <Button
            onClick={onCollapse}
            size="sm"
            variant="ghost"
            className="flex-shrink-0 h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground dark:text-border dark:hover:bg-sidebar-accent dark:hover:text-sidebar-accent-foreground"
            title="Collapse Sidebar"
          >
            <PanelLeftClose className="!size-5" />
          </Button>
        )}
      </div>

            {/* New Chat Button */}
      <div className="px-3 py-6">
        <Button
          onClick={handleNewChat}
          className="w-full flex items-center justify-center gap-2 min-h-[36px] bg-card text-foreground border border-border hover:bg-sidebar-accent hover:text-card-foreground transition-all"
          variant="default"
        >
          <EditSquare className="!size-4 flex-shrink-0 text-foreground" />
          <span className="truncate">New Chat</span>
        </Button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-2">
        
        {/* National Building Code */}
         <div className="space-y-1">
           <button
             onClick={handleNbcToggle}
             className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 group border ${
               nbcSelected
                 ? 'bg-primary text-primary-foreground border-sidebar-border'
                 : 'bg-card text-card-foreground border-border hover:bg-sidebar-accent hover:text-card-foreground'
             }`}
           >
             <div className={`w-4 h-4 flex items-center justify-center transition-colors ${
               nbcSelected ? 'text-primary-foreground' : 'text-primary/70 group-hover:text-primary'
             }`}>
               <Building2 className="w-4 h-4" />
             </div>
             <span className="text-sm font-medium">National Building Code</span>
           </button>
         </div>

        {/* State */}
        <div className="space-y-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 group border ${
                  selectedState
                    ? 'bg-primary text-primary-foreground border-sidebar-border'
                    : 'bg-card text-card-foreground border-border hover:bg-sidebar-accent hover:text-card-foreground'
                }`}
              >
                <div className={`w-4 h-4 flex items-center justify-center transition-colors ${
                  selectedState ? 'text-primary-foreground' : 'text-primary/70 group-hover:text-primary'
                }`}>
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  {selectedState ? (
                    <>
                      <div className="text-xs font-medium opacity-75">State</div>
                      <div className="text-sm text-primary-foreground">
                        {stateOptions.find(opt => opt.id === selectedState)?.name}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm font-medium">State</div>
                  )}
                </div>
                <div className="w-4 h-4 flex items-center justify-center">
                  <ChevronRight className={`w-3 h-3 transition-colors ${
                    selectedState ? 'text-primary' : 'text-muted-foreground group-hover:text-primary/70'
                  }`} />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              side="right" 
              align="start" 
              className="w-48 ml-2 bg-popover text-popover-foreground border-border"
              sideOffset={16}
            >
              <div className="px-3 py-2 border-b border-border mb-1">
                <div className="text-sm font-bold text-popover-foreground">State</div>
              </div>
              {stateOptions.map((option) => (
                <DropdownMenuItem
                  key={option.id}
                  onClick={() => handleStateSelect(option.id)}
                  className={`cursor-pointer transition-all duration-150 rounded-sm mx-1 ${
                    selectedState === option.id
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent/50 hover:!text-popover-foreground'
                  }`}
                >
                  <span className="text-sm font-medium">{option.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Transit Oriented Development */}
        <div className="space-y-1">
          <button
            onClick={handleTodToggle}
            className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 group border ${
              todSelected
                ? 'bg-primary text-primary-foreground border-sidebar-border'
                : 'bg-card text-card-foreground border-border hover:bg-sidebar-accent hover:text-card-foreground'
            }`}
          >
            <div className={`w-4 h-4 flex items-center justify-center transition-colors ${
              todSelected ? 'text-primary-foreground' : 'text-primary/70 group-hover:text-primary'
            }`}>
              <Train className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium">Transit Oriented Development</span>
          </button>
        </div>

        {/* Sustainability */}
        <div className="space-y-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 group border ${
                  selectedSustainability
                    ? 'bg-primary text-primary-foreground border-sidebar-border'
                    : 'bg-card text-card-foreground border-border hover:bg-sidebar-accent hover:text-card-foreground'
                }`}
              >
                <div className={`w-4 h-4 flex items-center justify-center transition-colors ${
                  selectedSustainability ? 'text-primary-foreground' : 'text-primary/70 group-hover:text-primary'
                }`}>
                  <Leaf className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  {selectedSustainability ? (
                    <>
                      <div className="text-xs font-medium opacity-75">Sustainability</div>
                      <div className="text-sm text-primary-foreground">
                        {sustainabilityOptions.find(opt => opt.id === selectedSustainability)?.name}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm font-medium">Sustainability</div>
                  )}
                </div>
                <div className="w-4 h-4 flex items-center justify-center">
                  <ChevronRight className={`w-3 h-3 transition-colors ${
                    selectedSustainability ? 'text-primary' : 'text-muted-foreground group-hover:text-primary/70'
                  }`} />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              side="right" 
              align="start" 
              className="w-48 ml-2 bg-popover text-popover-foreground border-border"
              sideOffset={16}
            >
              <div className="px-3 py-2 border-b border-border mb-1">
                <div className="text-sm font-bold text-popover-foreground">Sustainability</div>
              </div>
              {sustainabilityOptions.map((option) => (
                <DropdownMenuItem
                  key={option.id}
                  onClick={() => handleSustainabilitySelect(option.id)}
                  className={`cursor-pointer transition-all duration-150 rounded-sm mx-1 ${
                    selectedSustainability === option.id
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent/50 hover:!text-popover-foreground'
                  }`}
                >
                  <span className="text-sm font-medium">{option.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

export default function LeftSidebar({ open, onOpenChange, onCollapse, showCollapseButton }: LeftSidebarProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="w-80 p-0 bg-sidebar text-sidebar-foreground border-2 border-sidebar-border">
          <div className="flex flex-col h-full">
            <SidebarContent onCollapse={onCollapse} showCollapseButton={showCollapseButton} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="flex flex-col h-full relative bg-sidebar text-sidebar-foreground">
      <SidebarContent onCollapse={onCollapse} showCollapseButton={showCollapseButton} />
    </div>
  );
}